import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import studioReducer, { initialDesignPatternState, ACTIONS } from './reducers/studioReducer';
import { getSelectedMappings, updateMappingChecked, getConfigMapping } from './utils/mappingUtils';

// Create context
const DesignPatternStudioContext = createContext();

// Provider component
export const DesignPatternStudioProvider = ({ children }) => {
  const [state, dispatch] = useReducer(studioReducer, initialDesignPatternState);

  const selectedMappings = useMemo(() => {
    if (!state.mappingData) return [];
    return getSelectedMappings(state.mappingData, state.insideFeature);
  }, [state.mappingData, state.insideFeature]);

  const configMapping = useMemo(() => {
    if (!state.mappingData) return {};
    return getConfigMapping(state.mappingData, state.insideFeature);
  }, [state.mappingData, state.insideFeature]);

  const handleMappingChange = useCallback(
    (type, id, checked) => {
      if (!state.mappingData) return;
      const updatedData = updateMappingChecked(state.mappingData, type, id, checked);
      dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedData });
    },
    [state.mappingData, dispatch],
  );

  // Actions
  const setFile = (file) => {
    dispatch({ type: ACTIONS.SET_FILE, payload: file });
  };

  const setWarpedSvg = (warpedSvg) => {
    dispatch({ type: ACTIONS.SET_WARPED_SVG, payload: warpedSvg });
  };

  const resetState = () => {
    dispatch({ type: ACTIONS.RESET_STATE });
  };

  // Context value
  const value = {
    state,
    selectedMappings,
    configMapping,
    handleMappingChange,
    setFile,
    setWarpedSvg,
    resetState,
    dispatch,
  };

  return (
    <DesignPatternStudioContext.Provider value={value}>
      {children}
    </DesignPatternStudioContext.Provider>
  );
};

// Custom hook to use the context
export const useDesignPatternStudio = () => {
  const context = useContext(DesignPatternStudioContext);
  if (context === undefined) {
    throw new Error('useDesignPatternStudio must be used within a DesignPatternStudioProvider');
  }
  return context;
}; 